import { createContext, useContext, useState, useEffect } from "react";
import { setVendorIsApproved } from "../../redux/slice/upSwapSlice";
import { useDispatch, useSelector } from "react-redux";
import { useGetVendorKYCStatusQuery } from "../../redux/service/vendorService";

const AuthContext = createContext(null);

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [userRole, setUserRole] = useState("loading");

  const [canPostDeals, setCanPostDeals] = useState(false);
  const vendorId = localStorage.getItem("vendor_id");

  const [kycStatus, setKycStatus] = useState(null);
  const dispatch = useDispatch();
  const {
    data: kycData,
    isLoading: kycDataIsLoading,
    isFetching: kycDataIsFetching,
  } = useGetVendorKYCStatusQuery(vendorId, { skip: !vendorId });

  useEffect(() => {
    if (!kycDataIsFetching && !kycDataIsLoading) {
      dispatch(setVendorIsApproved(kycData?.is_approved));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycDataIsLoading, kycDataIsFetching, kycData]);

  const { vendorIsApproved } = useSelector((state) => state.upSwap);

  const determineUserRole = (data) => {
    if (!data) {
      return "public";
    }
    if (data.vendor_id && data?.is_approved) {
      return "vendor";
    } else if (data.user && data.user.id) {
      return "user";
    }
    return "public";
  };

  const extractVendorSpecificData = (data, role) => {
    if (role === "vendor" && data) {
      setCanPostDeals(data.is_approved === true);
      setKycStatus(
        data.kyc_status || (data.is_approved ? "approved" : "not_submitted")
      );
    } else {
      setCanPostDeals(false);
      setKycStatus(null);
    }
  };

  useEffect(() => {
    const access = localStorage.getItem("access");
    const storedUserDataString = localStorage.getItem("userData");

    if (access && storedUserDataString) {
      try {
        const parsedUserData = JSON.parse(storedUserDataString);
        setUserData(parsedUserData);
        setIsAuthenticated(true);
        const role = determineUserRole(parsedUserData);
        setUserRole(role);

        extractVendorSpecificData(parsedUserData, role);
      } catch (error) {
        logout();
      }
    } else {
      setIsAuthenticated(false);

      setUserData(null);

      setUserRole("public");
      setCanPostDeals(false);
      setKycStatus(null);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = (data) => {
    if (data && data.access) {
      localStorage.setItem("access", data.access);
      if (data.refresh) {
        localStorage.setItem("refresh", data.refresh);
      }
      localStorage.setItem("userData", JSON.stringify(data));
      localStorage.setItem("phone_number", data.user?.phone_number || "");
      localStorage.setItem("email", data.user?.email || "");

      setIsAuthenticated(true);

      setUserData(data);
      const role = determineUserRole(data);
      setUserRole(role);

      extractVendorSpecificData(data, role);
    } else {
      logout();
    }
  };

  const logout = () => {
    localStorage.clear();
    setIsAuthenticated(false);
    setUserData(null);
    setUserRole("public");
    setCanPostDeals(false);
    setKycStatus(null);
    dispatch(setVendorIsApproved(false));
  };

  useEffect(() => {
    const storedUserDataString = localStorage.getItem("userData");
    const localRole = localStorage.getItem("userRole");
    const parsedUserData = JSON.parse(storedUserDataString);
    const role = determineUserRole(parsedUserData);
    if (localRole) {
      setUserRole(localRole);
    } else {
      setUserRole(role);
    }
  }, [vendorIsApproved, kycDataIsLoading, kycDataIsFetching, kycData]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        loading,
        userData,
        userRole,
        canPostDeals,
        kycStatus,
        setUserRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
